import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  LinearProgress,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material'
import { Table } from 'shared/ui/Table/Table'
import { Column } from 'shared/ui/Table/types'
import {
  DistrictSchool,
  ICurricula,
  ISchoolGrouping,
  ISchoolGroupingMap,
} from 'infrastructure/api'
import { useNotify } from 'shared/ui/Notify'
import RequestDialog from '../../dialogs/RequestDialog'
import {
  useDeleteSchoolsCurriculaByDistrictID,
  useDeleteSchoolCurriculaExported,
} from '../../tree/TreeActions'
import { useGetDistrictGrouping } from '../curricula_components/utility'
import { doInBatches } from '../../../utility'

interface CurriculumSelection {
  curriculum_id: number
  tree_id: string
  tree_version: string
  title: string
}

interface BulkRemoveCurriculumProps {
  districtId: number
  districtSchools: DistrictSchool[]
  onComplete: () => void
}

const steps = [
  'Select Curricula to Remove',
  'Select Schools',
  'Confirm & Remove',
]

export function BulkRemoveCurriculum({
  districtId,
  districtSchools,
  onComplete,
}: BulkRemoveCurriculumProps) {
  const [activeStep, setActiveStep] = useState(0)
  const [selectedCurricula, setSelectedCurricula] = useState(
    new Map<string, CurriculumSelection>(),
  )
  const [selectedSchoolIndexes, setSelectedSchoolIndexes] = useState<number[]>(
    [],
  )
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [removing, setRemoving] = useState(false)
  const [progress, setProgress] = useState({ current: 0, total: 0 })
  const [loading, setLoading] = useState(true)
  const [groupingMap, setGroupingMap] = useState<ISchoolGroupingMap>({})
  const [sortedKeys, setSortedKeys] = useState<string[]>([])

  const notify = useNotify()
  const deleteByDistrict = useDeleteSchoolsCurriculaByDistrictID()
  const deleteSchoolCurricula = useDeleteSchoolCurriculaExported()
  const getDistrictGrouping = useGetDistrictGrouping()

  // Fetch district curricula on mount
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    getDistrictGrouping(districtId).then((result) => {
      if (cancelled) return
      setGroupingMap(result.grouping)
      setSortedKeys(result.sortedKeys)
      setLoading(false)
    })
    return () => {
      cancelled = true
    }
  }, [districtId, getDistrictGrouping])

  // Build a flat list of non-deleted curricula from the grouping
  const availableCurricula = useMemo(() => {
    const items: Array<{
      groupName: string
      grouping: ISchoolGrouping
      curriculum: ICurricula
    }> = []
    const allKeys = [...sortedKeys]
    if (groupingMap['Other']) {
      allKeys.push('Other')
    }
    for (const groupName of allKeys) {
      const groupings = groupingMap[groupName] ?? []
      for (const g of groupings) {
        for (const c of g.curriculaCopies) {
          if (!c.deleted) {
            items.push({ groupName, grouping: g, curriculum: c })
          }
        }
      }
    }
    return items
  }, [groupingMap, sortedKeys])

  // --- Curriculum selection ---
  const toggleCurriculum = useCallback(
    (curriculum: ICurricula, title: string) => {
      const key = `${title}:${curriculum.tree_id}:${curriculum.tree_version}`
      setSelectedCurricula((prev) => {
        const next = new Map(prev)
        if (next.has(key)) {
          next.delete(key)
        } else {
          next.set(key, {
            curriculum_id: curriculum.curriculum_id,
            tree_id: curriculum.tree_id,
            tree_version: curriculum.tree_version,
            title,
          })
        }
        return next
      })
    },
    [],
  )

  const removeCurriculumSelection = useCallback((key: string) => {
    setSelectedCurricula((prev) => {
      const next = new Map(prev)
      next.delete(key)
      return next
    })
  }, [])

  // --- School selection ---
  const onSchoolSelect = useCallback(
    (index: number) => {
      if (index === -1) {
        if (selectedSchoolIndexes.length === districtSchools.length) {
          setSelectedSchoolIndexes([])
        } else {
          setSelectedSchoolIndexes(districtSchools.map((_s, i) => i))
        }
      } else {
        setSelectedSchoolIndexes((prev) =>
          prev.includes(index)
            ? prev.filter((i) => i !== index)
            : [...prev, index],
        )
      }
    },
    [districtSchools, selectedSchoolIndexes.length],
  )

  const schoolColumns: Column<DistrictSchool>[] = useMemo(
    () => [
      {
        title: <strong>School Name</strong>,
        width: 250,
        rowContent(school: DistrictSchool) {
          return school.name ?? `School ${school.id}`
        },
      },
      {
        title: <strong>ID</strong>,
        width: 60,
        rowContent(school: DistrictSchool) {
          return school.id
        },
      },
      {
        title: <strong>Teachers</strong>,
        width: 80,
        rowContent(school: DistrictSchool) {
          return school.teacher_count
        },
      },
      {
        title: <strong>Students</strong>,
        width: 80,
        rowContent(school: DistrictSchool) {
          return school.student_count
        },
      },
    ],
    [],
  )

  const selectedSchools = useMemo(
    () => selectedSchoolIndexes.map((i) => districtSchools[i]),
    [selectedSchoolIndexes, districtSchools],
  )

  const allSchoolsSelected =
    selectedSchoolIndexes.length === districtSchools.length &&
    districtSchools.length > 0

  // --- Submit ---
  const handleRemove = useCallback(async () => {
    setConfirmOpen(false)
    setRemoving(true)

    const curricula = Array.from(selectedCurricula.values())

    if (allSchoolsSelected) {
      // Strategy 1: batch delete per curriculum via district-level endpoint
      const total = curricula.length
      setProgress({ current: 0, total })

      let successCount = 0
      let errorCount = 0

      for (let i = 0; i < curricula.length; i++) {
        const curr = curricula[i]
        try {
          await deleteByDistrict({
            master_tree_id: curr.tree_id,
            master_version_id: curr.tree_version,
            district_id: districtId,
          })
          successCount++
        } catch {
          errorCount++
        }
        setProgress((prev) => ({ ...prev, current: prev.current + 1 }))
      }

      setRemoving(false)
      if (errorCount === 0) {
        notify(
          `Successfully removed ${successCount} curricula from all schools in district.`,
          { type: 'info' },
        )
      } else {
        notify(
          `Removed ${successCount} curricula, ${errorCount} failed. Check individual schools for details.`,
          { type: 'error' },
        )
      }
    } else {
      // Strategy 2: per-school granular delete
      const pairs = curricula.flatMap((c) =>
        selectedSchools.map((s) => ({ curriculum: c, school: s })),
      )
      const total = pairs.length
      setProgress({ current: 0, total })

      let successCount = 0
      let errorCount = 0

      await doInBatches(
        pairs,
        async ({ curriculum, school }) => {
          try {
            await deleteSchoolCurricula({
              curricula_id: curriculum.curriculum_id,
              school_id: school.id,
              deleted: true,
            })
            successCount++
          } catch {
            errorCount++
          }
          setProgress((prev) => ({ ...prev, current: prev.current + 1 }))
        },
        10,
      )

      setRemoving(false)
      if (errorCount === 0) {
        notify(
          `Successfully removed ${curricula.length} curricula from ${selectedSchools.length} schools.`,
          { type: 'info' },
        )
      } else {
        notify(
          `Removed from ${successCount} school-curriculum pairs, ${errorCount} failed.`,
          { type: 'error' },
        )
      }
    }

    // Reset state
    setSelectedCurricula(new Map())
    setSelectedSchoolIndexes([])
    setActiveStep(0)
    onComplete()
  }, [
    selectedCurricula,
    allSchoolsSelected,
    selectedSchools,
    deleteByDistrict,
    deleteSchoolCurricula,
    districtId,
    notify,
    onComplete,
  ])

  // --- Navigation ---
  const canNext =
    (activeStep === 0 && selectedCurricula.size > 0) ||
    (activeStep === 1 && selectedSchoolIndexes.length > 0)

  const handleNext = () => {
    if (activeStep === 2) {
      setConfirmOpen(true)
    } else {
      setActiveStep((s) => s + 1)
    }
  }

  const handleBack = () => setActiveStep((s) => s - 1)

  if (removing) {
    const pct =
      progress.total > 0
        ? Math.round((progress.current / progress.total) * 100)
        : 0
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Removing curricula from schools...
        </Typography>
        <LinearProgress variant="determinate" value={pct} sx={{ mb: 1 }} />
        <Typography variant="body2" color="text.secondary">
          {progress.current} of {progress.total}{' '}
          {allSchoolsSelected ? 'curricula' : 'operations'}
        </Typography>
      </Box>
    )
  }

  if (loading) {
    return (
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={20} />
        <Typography variant="body2">Loading district curricula...</Typography>
      </Box>
    )
  }

  if (availableCurricula.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info">
          No active curricula found in this district.
        </Alert>
      </Box>
    )
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Stepper activeStep={activeStep} sx={{ mb: 2 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Step 1: Select Curricula to Remove */}
      {activeStep === 0 && (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Select the curricula to remove from schools. These are curricula
            currently assigned in this district.
          </Typography>
          <Box
            sx={{
              maxHeight: 400,
              overflowY: 'auto',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              p: 1,
            }}
          >
            {[...sortedKeys, ...(groupingMap['Other'] ? ['Other'] : [])].map(
              (groupName) => {
                const groupings = groupingMap[groupName] ?? []
                const nonDeletedGroupings = groupings.filter((g) =>
                  g.curriculaCopies.some((c) => !c.deleted),
                )
                if (nonDeletedGroupings.length === 0) return null
                return (
                  <Box key={groupName} sx={{ mb: 1 }}>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      sx={{ mb: 0.5 }}
                    >
                      {groupName}
                    </Typography>
                    {nonDeletedGroupings.map((g) =>
                      g.curriculaCopies
                        .filter((c) => !c.deleted)
                        .map((c) => {
                          const key = `${g.master.title}:${c.tree_id}:${c.tree_version}`
                          const isSelected = selectedCurricula.has(key)
                          return (
                            <Box
                              key={key}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                                '&:hover': { bgcolor: 'action.hover' },
                                borderRadius: 1,
                                px: 1,
                              }}
                              onClick={() =>
                                toggleCurriculum(c, g.master.title)
                              }
                            >
                              <Checkbox
                                checked={isSelected}
                                size="small"
                                tabIndex={-1}
                              />
                              <Typography variant="body2">
                                {g.master.title} (v{c.tree_version})
                              </Typography>
                            </Box>
                          )
                        }),
                    )}
                  </Box>
                )
              },
            )}
          </Box>
          {selectedCurricula.size > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="subtitle2">
                Selected for removal ({selectedCurricula.size}):
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {Array.from(selectedCurricula.entries()).map(([key, val]) => (
                  <Chip
                    key={key}
                    label={val.title}
                    size="small"
                    color="error"
                    onDelete={() => removeCurriculumSelection(key)}
                  />
                ))}
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* Step 2: Select Schools */}
      {activeStep === 1 && (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Select the schools to remove the curricula from. Use the header
            checkbox to select all.
          </Typography>
          <Box sx={{ mb: 1 }}>
            <Checkbox
              checked={allSchoolsSelected}
              indeterminate={
                selectedSchoolIndexes.length > 0 &&
                selectedSchoolIndexes.length < districtSchools.length
              }
              onChange={() => onSchoolSelect(-1)}
              size="small"
            />
            <Typography variant="body2" component="span">
              Select All ({selectedSchoolIndexes.length} of{' '}
              {districtSchools.length})
            </Typography>
          </Box>
          <Table
            columns={schoolColumns}
            items={districtSchools}
            onRowSelect={onSchoolSelect}
            selectedRows={selectedSchoolIndexes}
            overrideHeight={Math.min((districtSchools.length + 1) * 33, 400)}
          />
          {allSchoolsSelected && (
            <Alert severity="info" sx={{ mt: 1 }}>
              All schools selected — will use batch removal (faster).
            </Alert>
          )}
        </Box>
      )}

      {/* Step 3: Confirm */}
      {activeStep === 2 && (
        <Box>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Review the removal below before confirming. This action will
            soft-delete the selected curricula.
          </Alert>
          <Typography variant="subtitle2" gutterBottom>
            Curricula to remove ({selectedCurricula.size}):
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
            {Array.from(selectedCurricula.values()).map((val, i) => (
              <Chip key={i} label={val.title} size="small" color="error" />
            ))}
          </Box>
          <Typography variant="subtitle2" gutterBottom>
            From schools ({selectedSchools.length}):
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {selectedSchools.map((s) => (
              <Chip
                key={s.id}
                label={s.name ?? `School ${s.id}`}
                size="small"
              />
            ))}
          </Box>
          <Alert severity="error" sx={{ mt: 2 }}>
            This will remove {selectedCurricula.size} curricula from{' '}
            {selectedSchools.length} schools
            {allSchoolsSelected
              ? ' using batch removal.'
              : ` (${selectedCurricula.size * selectedSchools.length} total operations).`}
          </Alert>
        </Box>
      )}

      {/* Navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
        <Button
          disabled={activeStep === 0}
          onClick={handleBack}
          variant="outlined"
        >
          Back
        </Button>
        <Button
          disabled={!canNext && activeStep !== 2}
          onClick={handleNext}
          variant="contained"
          color={activeStep === 2 ? 'error' : 'primary'}
        >
          {activeStep === 2 ? 'Remove Curricula' : 'Next'}
        </Button>
      </Box>

      <RequestDialog
        open={confirmOpen}
        title={`Remove ${selectedCurricula.size} curricula from ${selectedSchools.length} schools?`}
        content={[]}
        submitRequest={handleRemove}
        onClose={() => setConfirmOpen(false)}
      />
    </Box>
  )
}
